import React, { useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';

// YouTube URL'den video ID'sini çıkaran yardımcı fonksiyon
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  
  return (match && match[2].length === 11) ? match[2] : null;
};

const YouTubeModal = ({ isOpen, onClose, videoUrl, productName }) => {
  const videoId = getYouTubeVideoId(videoUrl);
  const { t } = useTranslation();

  // ESC tuşu ile kapatma
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden'; // Scroll'u engelle
    }
    
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !videoId) return null;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black animate-fadeIn"
      onClick={onClose}
    >
      <div 
        className="relative w-full h-full bg-black flex flex-col animate-modalSlideIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Fullscreen Header - Floating and Minimal */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-6 bg-gradient-to-b from-black/70 to-transparent">
          <div className="flex items-center space-x-3">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
            <h3 className="text-xl font-bold text-white truncate">
              {productName ? `${productName}` : t('common.productVideo') || 'Mahsulot videosi'}
            </h3>
          </div>
          <button 
            onClick={onClose}
            className="group flex items-center justify-center w-12 h-12 text-white hover:text-red-400 hover:bg-white/10 rounded-full transition-all duration-300 flex-shrink-0 ml-4 hover:rotate-90"
          >
            <svg className="w-7 h-7 transition-transform duration-300 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Fullscreen Video Container */}
        <div className="relative flex-1 w-full h-full bg-black flex items-center justify-center">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&rel=0&modestbranding=1&playsinline=1&fs=1&controls=1&enablejsapi=1`}
            title={productName || 'YouTube Video'}
            className="w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
            allowFullScreen
          ></iframe>
          
          {/* Loading indicator overlay (shows while iframe loads) */}
          <div className="absolute inset-0 flex items-center justify-center bg-black pointer-events-none">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-12 h-12 border-3 border-white/20 border-t-white rounded-full animate-spin"></div>
              <p className="text-white text-lg font-medium">{t('common.videoLoading') || 'Video yuklanmoqda...'}</p>
            </div>
          </div>
        </div>

        {/* Fullscreen Footer - Floating Bottom Controls */}
        <div className="absolute bottom-0 left-0 right-0 z-10 p-6 bg-gradient-to-t from-black/70 to-transparent">
          <div className="flex items-center justify-center">
            <a 
              href={videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl font-medium text-sm transition-all duration-200 hover:shadow-2xl transform hover:scale-105 backdrop-blur-sm"
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
{t('common.openOnYouTube') || 'YouTube\'da ochish'}
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.98);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        
        .animate-modalSlideIn {
          animation: modalSlideIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default YouTubeModal;